<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'

import { Share, FileDown, FileUp, Plus, ArrowRightToLine } from 'lucide-vue-next'

import Tooltip from '@/components/Tooltip.vue'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

const props = defineProps({
    list: {
        type: Object,
        required: false,
    },
    platformId: {
        type: String,
        required: false,
    }
})

const store = useStore()
const { checkPermission, isLegacyBilling, quotaRemaining } = store.getters

const open = ref(false)

const isExportEnabled = computed(() => checkPermission('feature.lists.export'))
const isReportsEnabled = computed(() => checkPermission('module.reports'))

const insufficientQuota = computed(() => {
    return !isLegacyBilling && ( quotaRemaining({ quotaType : 'reportGeneration'}) <= 0 )
})

const emit = defineEmits(['export-creators', 'import-creators', 'share', 'add-creators'])
</script>

<template>
    <div class="flex w-full flex-col items-start justify-between sm:flex-row sm:items-center">
        <DropdownMenu v-model:open="open">
            <DropdownMenuTrigger>
                <button
                    class="btn flex px-3 py-2.5 cursor-pointer overflow-hidden rounded border-slate-200 bg-slate-50 uppercase shadow-none hover:border-slate-300 hover:bg-slate-100 active:border-slate-300 active:bg-slate-100"
                >
                    <AppIcon name="ellipsis" class="h-4 w-4" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-[200px]">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuGroup>
                    <DropdownMenuItem
                        v-if="props.list.isOperatorList === true"
                        class="cursor-pointer"
                        @click="emit('add-creators')"
                    >
                        <Plus class="mr-2 h-4 w-4" />
                        Add creator(s)
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        v-if="props.list.isOperatorList === true"
                        class="cursor-pointer"
                        @click="emit('import-creators')"
                    >
                        <FileDown class="mr-2 h-4 w-4" />
                        Import creators
                    </DropdownMenuItem>
                    <DropdownMenuItem v-if="isExportEnabled" class="cursor-pointer" @click="emit('export-creators')">
                        <FileUp class="mr-2 h-4 w-4" />
                        Export creators
                    </DropdownMenuItem>
                    <Tooltip v-else placement="left">
                        <template #trigger>
                            <DropdownMenuItem
                                :disabled="!isExportEnabled"
                                class="cursor-pointer"
                                @click="emit('export-creators')"
                            >
                                <FileUp class="mr-2 h-4 w-4" />
                                Export creators
                            </DropdownMenuItem>
                        </template>
                        <template #default>
                            <div class="whitespace-normal text-xs">
                                Feature Unavailable - Account upgrade required for access to list exports.
                            </div>
                        </template>
                    </Tooltip>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem class="cursor-pointer" @click="emit('share')">
                        <Share class="mr-2 h-4 w-4" />
                        Share
                    </DropdownMenuItem>
                    <template v-if="isReportsEnabled && platformId && list?.totals?.total > 0">
                        <DropdownMenuSeparator />
                         <Tooltip :show-tooltip="insufficientQuota">
                            <template #trigger>
                                <DropdownMenuItem class="cursor-pointer" :disabled="insufficientQuota">
                                    <router-link
                                        :to="{
                                            name: 'reports',
                                            query: {
                                                list_id: list.id,
                                                platform_id: platformId,
                                            },
                                        }"
                                    >
                                        <div class="flex">
                                            <ArrowRightToLine class="mr-2 h-4 w-4 mt-1" />
                                            <span>Create report</span>
                                        </div>
                                    </router-link>
                                </DropdownMenuItem>
                            </template>
                            <template #default>
                                <div class="whitespace-normal text-xs">Please ensure you have enough quota to generate a report.</div>
                            </template>
                        </Tooltip>

                    </template>
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    </div>
</template>
